import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TextField,
  InputAdornment, Chip, IconButton, Button, AppBar, Toolbar,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, List, ListItem, ListItemText, Select,
  MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Store as StoreIcon,
  ExitToApp as LogoutIcon,
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  OpenInNew as OpenIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

function PlatformTenants() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const token = localStorage.getItem('platform_token');
  const admin = JSON.parse(localStorage.getItem('platform_admin') || '{}');

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/platform'); return; }
    fetchTenants();
  }, [token, page, rowsPerPage, search, statusFilter]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter })
      };
      const res = await api.get('/platform/tenants', { headers, params });
      setTenants(res.data.data.items || []);
      setTotal(res.data.data.pagination?.total || 0);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('platform_token');
        navigate('/platform');
      } else {
        setError(t('platform.tenants.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (tenant) => {
    try {
      const res = await api.get(`/platform/tenants/${tenant.id}`, { headers });
      setSelectedTenant(res.data.data);
      setDetailOpen(true);
    } catch (err) {
      console.error('Failed to load tenant detail:', err);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await api.put(`/platform/tenants/${id}/status`, { status: newStatus }, { headers });
      fetchTenants();
      if (detailOpen && selectedTenant?.id === id) {
        setSelectedTenant(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_admin');
    navigate('/platform');
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('zh-TW', {
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <StoreIcon sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('platform.nav.title')}</Typography>
          <Button color="inherit" component={Link} to="/platform/dashboard" startIcon={<DashboardIcon />}>
            {t('platform.nav.dashboard')}
          </Button>
          <Typography sx={{ mx: 2 }}>{admin.name || admin.username}</Typography>
          <IconButton color="inherit" onClick={fetchTenants}><RefreshIcon /></IconButton>
          <IconButton color="inherit" onClick={handleLogout}><LogoutIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>{t('platform.tenants.title')}</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Search & Filter */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder={t('platform.tenants.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('platform.tenants.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('platform.tenants.status')}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">{t('platform.common.all')}</MenuItem>
              <MenuItem value="active">{t('platform.status.active')}</MenuItem>
              <MenuItem value="pending">{t('platform.status.pending')}</MenuItem>
              <MenuItem value="suspended">{t('platform.status.suspended')}</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        {/* Tenants Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>ID</TableCell>
                  <TableCell>{t('platform.tenants.storeName')}</TableCell>
                  <TableCell>{t('platform.tenants.subdomain')}</TableCell>
                  <TableCell>{t('platform.tenants.plan')}</TableCell>
                  <TableCell align="center">{t('platform.tenants.productCount')}</TableCell>
                  <TableCell align="center">{t('platform.tenants.orderCount')}</TableCell>
                  <TableCell align="center">{t('platform.tenants.status')}</TableCell>
                  <TableCell>{t('platform.tenants.createdAt')}</TableCell>
                  <TableCell align="center">{t('platform.tenants.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : tenants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        {search ? t('platform.tenants.noSearchResult') : t('platform.tenants.noData')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tenants.map((tenant) => {
                    return (
                      <TableRow key={tenant.id} hover>
                        <TableCell>{tenant.id}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{tenant.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{tenant.admin_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary.main" fontWeight="medium">
                            {tenant.slug}.urstore.tw
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={tenant.plan_name || t('platform.common.free')} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">{tenant.product_count || 0}</TableCell>
                        <TableCell align="center">{tenant.order_count || 0}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t(`platform.status.${tenant.status}`, { defaultValue: tenant.status })}
                            color={{ active: 'success', pending: 'warning', suspended: 'error', trial: 'info' }[tenant.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(tenant.created_at)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleViewDetail(tenant)} title={t('platform.tenants.viewDetail')}>
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(tenant.id, tenant.status)}
                            color={tenant.status === 'suspended' ? 'success' : 'error'}
                            title={tenant.status === 'suspended' ? t('platform.tenants.activate') : t('platform.tenants.suspend')}
                          >
                            {tenant.status === 'suspended' ? <ActiveIcon /> : <BlockIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={t('platform.common.rowsPerPage')}
          />
        </Paper>
      </Container>

      {/* Tenant Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StoreIcon color="primary" />
            <Box>
              <Typography variant="h6">{selectedTenant?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTenant?.slug}.urstore.tw
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTenant && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('platform.tenants.basicInfo')}</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary={t('platform.tenants.tenantId')} secondary={selectedTenant.id} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Slug" secondary={selectedTenant.slug} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary={t('platform.tenants.adminEmail')} secondary={selectedTenant.admin_email || '-'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary={t('platform.tenants.plan')} secondary={selectedTenant.plan_name || t('platform.common.free')} />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('platform.tenants.status')}
                        secondary={
                          <Chip
                            label={t(`platform.status.${selectedTenant.status}`, { defaultValue: selectedTenant.status })}
                            color={{ active: 'success', pending: 'warning', suspended: 'error', trial: 'info' }[selectedTenant.status] || 'default'}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('platform.tenants.dateInfo')}</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary={t('platform.tenants.createdAt')} secondary={formatDate(selectedTenant.created_at)} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary={t('platform.tenants.trialEnds')} secondary={formatDate(selectedTenant.trial_ends_at)} />
                    </ListItem>
                  </List>
                </Paper>

                {/* Domain List */}
                {selectedTenant.domains?.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('platform.tenants.domains')}</Typography>
                    <List dense>
                      {selectedTenant.domains.map((d, i) => (
                        <ListItem key={i}>
                          <ListItemText
                            primary={d.domain}
                            secondary={d.is_primary ? t('platform.tenants.primaryDomain') : t('platform.tenants.secondaryDomain')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Grid>

              {/* Admin List */}
              {selectedTenant.admins?.length > 0 && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t('platform.tenants.admins')}</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('platform.tenants.account')}</TableCell>
                          <TableCell>{t('platform.tenants.name')}</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>{t('platform.tenants.role')}</TableCell>
                          <TableCell>{t('platform.tenants.lastLogin')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedTenant.admins.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>{a.username}</TableCell>
                            <TableCell>{a.name || '-'}</TableCell>
                            <TableCell>{a.email || '-'}</TableCell>
                            <TableCell>{a.role}</TableCell>
                            <TableCell>{formatDate(a.last_login_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTenant && (
            <Button
              startIcon={<OpenIcon />}
              href={`/?preview_tenant=${selectedTenant.slug}`}
              target="_blank"
            >
              {t('platform.tenants.goToStore')}
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>{t('platform.common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlatformTenants;
